import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Filter, 
  Search, 
  Grid3X3, 
  List, 
  TrendingUp,
  Clock,
  DollarSign,
  Flame
} from 'lucide-react';
import { MarketCard } from '../components/MarketCard';
import { mockMarkets } from '../utils/mockData';
import type { MarketCategory } from '../types';

const categories: { value: MarketCategory | ''; label: string }[] = [
  { value: '', label: 'All Markets' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'sports', label: 'Sports' },
  { value: 'politics', label: 'Politics' },
  { value: 'finance', label: 'Finance' },
  { value: 'ai', label: 'AI' },
  { value: 'social', label: 'Social' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'science', label: 'Science' },
];

const sortOptions = [
  { value: 'volume', label: 'Highest Volume' },
  { value: 'newest', label: 'Newest' },
  { value: 'ending', label: 'Ending Soon' },
  { value: 'liquidity', label: 'Most Liquid' },
];

const statusFilters = [
  { value: 'all', label: 'All', icon: Grid3X3 },
  { value: 'active', label: 'Active', icon: Flame },
  { value: 'trending', label: 'Trending', icon: TrendingUp },
  { value: 'ending', label: 'Ending Soon', icon: Clock },
];

export function Markets() {
  const [selectedCategory, setSelectedCategory] = useState<MarketCategory | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('volume');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredMarkets = useMemo(() => {
    let filtered = [...mockMarkets];

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(m => m.category === selectedCategory);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.title.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter === 'trending') {
      filtered = filtered.filter(m => m.isTrending);
    } else if (statusFilter === 'ending') {
      filtered = filtered.sort((a, b) => 
        new Date(a.endTime).getTime() - new Date(b.endTime).getTime()
      );
    }

    // Sort
    switch (sortBy) {
      case 'volume':
        filtered.sort((a, b) => b.totalVolume - a.totalVolume);
        break;
      case 'newest':
        filtered.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        break;
      case 'liquidity':
        filtered.sort((a, b) => b.totalLiquidity - a.totalLiquidity);
        break;
    }

    return filtered;
  }, [selectedCategory, searchQuery, sortBy, statusFilter]);

  const stats = {
    total: filteredMarkets.length,
    volume: filteredMarkets.reduce((sum, m) => sum + m.totalVolume, 0),
    liquidity: filteredMarkets.reduce((sum, m) => sum + m.totalLiquidity, 0),
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-white mb-4">Prediction Markets</h1>
            <p className="text-gray-400 max-w-2xl">
              Trade on the outcome of future events. From crypto prices to political elections, 
              find markets that match your expertise.
            </p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-8 flex flex-wrap gap-6"
          >
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <Grid3X3 className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Markets</p>
                <p className="text-lg font-semibold text-white">{stats.total}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Volume</p>
                <p className="text-lg font-semibold text-white">${(stats.volume / 1e6).toFixed(2)}M</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Liquidity</p>
                <p className="text-lg font-semibold text-white">${(stats.liquidity / 1e6).toFixed(2)}M</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="sticky top-16 z-40 glass border-y border-omni-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search markets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-omni-card border border-omni-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-omni-card border border-omni-border rounded-xl text-white focus:outline-none focus:border-indigo-500 cursor-pointer"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {/* View Toggle */}
            <div className="flex bg-omni-card rounded-xl p-1 border border-omni-border">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCategory === cat.value
                    ? 'bg-indigo-500 text-white'
                    : 'bg-omni-card text-gray-400 hover:text-white border border-omni-border'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Status Filters */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-omni-border">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  statusFilter === filter.value
                    ? 'bg-white/10 text-white'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <filter.icon className="w-4 h-4" />
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Markets Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredMarkets.length > 0 ? (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredMarkets.map((market, index) => (
              <MarketCard key={market.id} market={market} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-omni-card flex items-center justify-center">
              <Search className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No markets found</h3>
            <p className="text-gray-500">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>
    </div>
  );
}
